import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { useMediaQuery } from 'react-responsive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { showSuccessThunk } from '@spike/notifications-action';
import Button from 'components/UI/V3/Button/Button';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { toEmailLocation } from 'components/EmailMarketing/apiAdapters';
import { describeLocations } from 'components/EmailMarketing/utils';
import { createDataExportThunk, fetchDataExportsThunk } from 'actions/dataExports/DataExportsActions';
import { fetchEmailMarketingLocationsThunk } from 'actions/emailMarketing/EmailMarketingActions';
import { CreateDataExport, DataExport, MAX_RUNNING_DATA_EXPORTS, isDataExportRunning } from 'model/dataExports';
import useOrganizationId from 'hooks/useOrganizationId';
import { RootState } from 'store';
import { RootAction } from 'actions/ActionTypes';
import DataExportCard from './Card/DataExportCard';
import NewDataExportDrawer from './New/NewDataExportDrawer';
import styles from './DataExportContent.module.scss';

const POLL_INTERVAL_MS = 4000;
const SKELETON_ROWS = 3;

export const DataExportContent: React.FC = () => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, RootAction>>();
    const organizationId = useOrganizationId();
    const isMobile = useMediaQuery({ maxWidth: 600 });

    const { dataExports: allDataExports, loading, creating } = useSelector((state: RootState) => state.dataExports);
    const { locations: apiLocations, locationsLoaded } = useSelector((state: RootState) => state.emailMarketing);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [highlightedId, setHighlightedId] = useState<string | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);

    // Failed exports are not shown to customers; they can start a new one from the drawer.
    const dataExports = useMemo(
        () => allDataExports.filter(dataExport => dataExport.status !== 'failed'),
        [allDataExports]
    );
    const locations = useMemo(() => apiLocations.map(toEmailLocation), [apiLocations]);
    const runningCount = dataExports.filter(isDataExportRunning).length;
    const limitReached = runningCount >= MAX_RUNNING_DATA_EXPORTS;

    const loadExports = () => {
        if (!organizationId) return;

        setLoadFailed(false);
        dispatch(fetchDataExportsThunk(organizationId)).catch(() => setLoadFailed(true));
    };

    useEffect(() => {
        if (organizationId) {
            loadExports();

            if (!locationsLoaded) {
                dispatch(fetchEmailMarketingLocationsThunk(organizationId)).catch(() => undefined);
            }
        }
    }, [dispatch, organizationId]);

    // Poll only while something is being generated; stops once every export settles.
    useEffect(() => {
        if (!organizationId || runningCount === 0) return;

        const interval = setInterval(() => {
            dispatch(fetchDataExportsThunk(organizationId, { silent: true })).catch(() => undefined);
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [dispatch, organizationId, runningCount]);

    const createExport = async (dataExport: CreateDataExport): Promise<boolean> => {
        if (!organizationId) return false;

        try {
            const created = await dispatch(createDataExportThunk(organizationId, dataExport));
            setHighlightedId(created.id);
            dispatch(showSuccessThunk("Export started. It'll be ready to download here in a few minutes.") as any);
            return true;
        } catch (error) {
            // The thunk already shows the error toast; the drawer stays open to retry.
            return false;
        }
    };

    const rerun = (dataExport: DataExport) => {
        createExport({
            resource: dataExport.resource,
            locationIds: dataExport.locationIds,
            startDate: dataExport.startDate,
            endDate: dataExport.endDate
        });
    };

    const locationsLabel = (dataExport: DataExport): string => {
        if (dataExport.locationIds.length === 0) return 'All locations';
        if (locations.length === 0) {
            const count = dataExport.locationIds.length;
            return count === 1 ? '1 location' : `${count} locations`;
        }
        return describeLocations(dataExport.locationIds, locations);
    };

    const newExportButton = (
        <Button
            variant="outlined"
            size="large"
            leftIcon={faPlus}
            label="New export"
            disabled={limitReached || !organizationId}
            className={styles.newExportButton}
            onClick={() => setIsDrawerOpen(true)}
        />
    );

    const renderSkeleton = () => (
        <ul
            className={styles.list}
            aria-busy="true"
        >
            {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <li
                    key={index}
                    className={styles.skeletonRow}
                >
                    <span className={styles.skeletonTile} />
                    <span className={styles.skeletonLines}>
                        <span className={styles.skeletonLine} />
                        <span className={styles.skeletonLineShort} />
                    </span>
                </li>
            ))}
        </ul>
    );

    const renderEmptyState = () => (
        <div className={styles.emptyState}>
            <span className={styles.emptyStateIcon}>
                <FontAwesomeIcon icon={faFileExport} />
            </span>
            <p className={styles.emptyStateTitle}>No exports yet</p>
            <p className={styles.emptyStateText}>
                Export your clients, pets, appointments or invoices from every location in a single CSV file.
            </p>
            <Button
                variant="black"
                size="large"
                leftIcon={faPlus}
                label="Create your first export"
                disabled={!organizationId}
                onClick={() => setIsDrawerOpen(true)}
            />
        </div>
    );

    const renderError = () => (
        <div className={styles.emptyState}>
            <p className={styles.emptyStateTitle}>We couldn&apos;t load your exports</p>
            <p className={styles.emptyStateText}>Check your connection and try again.</p>
            <Button
                variant="outlined"
                size="large"
                label="Try again"
                onClick={loadExports}
            />
        </div>
    );

    const renderList = () => (
        <ul className={styles.list}>
            {dataExports.map(dataExport => (
                <DataExportCard
                    key={dataExport.id}
                    dataExport={dataExport}
                    locationsLabel={locationsLabel(dataExport)}
                    highlighted={dataExport.id === highlightedId}
                    rerunDisabled={limitReached || creating}
                    onRerun={rerun}
                />
            ))}
        </ul>
    );

    const renderContent = () => {
        if (loading && dataExports.length === 0) return renderSkeleton();
        if (loadFailed && dataExports.length === 0) return renderError();
        if (dataExports.length === 0) return renderEmptyState();
        return renderList();
    };

    return (
        <div className={styles.dataExportContent}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>Data export</h1>
                    <p className={styles.subtitle}>
                        Download your organization&apos;s data as CSV files. Files are generated in the background and
                        stay available for 24 hours.
                    </p>
                </div>

                {dataExports.length > 0 && (
                    <div className={styles.headerAction}>
                        {limitReached ? (
                            <Tooltip
                                content={`You can run up to ${MAX_RUNNING_DATA_EXPORTS} exports at a time. Wait for one to finish.`}
                                placement={isMobile ? 'top' : 'left'}
                            >
                                <span className={styles.tooltipTrigger}>{newExportButton}</span>
                            </Tooltip>
                        ) : (
                            newExportButton
                        )}
                    </div>
                )}
            </div>

            <div className={styles.content}>{renderContent()}</div>

            {isDrawerOpen && (
                <NewDataExportDrawer
                    locations={locations}
                    locationsLoading={!locationsLoaded}
                    creating={creating}
                    onSubmit={createExport}
                    onClose={() => setIsDrawerOpen(false)}
                />
            )}
        </div>
    );
};

export default DataExportContent;
